import { z } from 'zod';

/** One mini-league's standing for the manager whose `RankContext` this sits
 * in — read straight off `entry/{id}/`'s `leagues.classic` array, not a
 * standings-page fetch. `rank`/`previousRank` are nullable: FPL omits them
 * for a league the manager only just joined. */
export const RankLeagueStanding = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  rank: z.number().int().nullable(),
  previousRank: z.number().int().nullable(),
});
export type RankLeagueStanding = z.infer<typeof RankLeagueStanding>;

/**
 * Where a manager actually stands, read from `entry/{id}/` rather than
 * assumed from expected points. Every numeric field FPL might not have yet
 * (a manager's first gameweek, before any rank has been calculated) is
 * nullable — that is a normal state, not an error.
 */
export const RankContext = z.object({
  overallRank: z.number().int().nullable(),
  totalPlayers: z.number().int().nullable(),
  /** The manager's overall rank as a rounded percentage of the field — the
   * number a human reads as "top 4%". Null whenever `overallRank` or
   * `totalPlayers` is null, never a figure derived from a missing
   * denominator. */
  percentile: z.number().nullable(),
  /** Gameweeks left to play, counting the current one as still to play.
   * Never negative. */
  gameweeksRemaining: z.number().int().min(0),
  leagues: z.array(RankLeagueStanding),
});
export type RankContext = z.infer<typeof RankContext>;
